import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { sanitizeChallengeHtml } from '../../utils/challengeHtml';
import {
  ArrowLeft,
  Loader2,
  Send,
  CheckCircle2,
  XCircle,
  Lock,
} from 'lucide-react';

const StudentChallengeDetail = () => {
  const { challengeId } = useParams();
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadView = useCallback(async () => {
    if (!challengeId) return;
    setLoading(true);
    try {
      const res = await api.get(`/challenges/${challengeId}/student-view`);
      if (res.data?.success && res.data.data) {
        setData(res.data.data);
      } else {
        setData(null);
        toast.error(t('studentChallenges.viewError'));
      }
    } catch (e) {
      console.error(e);
      setData(null);
      toast.error(t('studentChallenges.viewError'));
    } finally {
      setLoading(false);
    }
  }, [challengeId, t]);

  useEffect(() => {
    loadView();
  }, [loadView]);

  const challenge = data?.challenge;
  const submission = data?.submission;
  const readOnly = data?.readOnly === true;

  const canSubmit = !readOnly && !submission && challenge;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!challengeId || !canSubmit) return;
    const trimmed = answer.trim();
    if (!trimmed) {
      toast.error(t('studentChallenges.answerRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/challenges/${challengeId}/submissions`, { answer: trimmed });
      if (res.data?.success) {
        toast.success(res.data.message || t('studentChallenges.submitSuccess'));
        setAnswer('');
        await loadView();
      } else {
        toast.error(t('studentChallenges.submitError'));
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message;
      toast.error(msg || t('studentChallenges.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const html = challenge ? sanitizeChallengeHtml(challenge.description) : '';

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <Loader2 className="h-10 w-10 animate-spin text-violet-600" aria-hidden />
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16" dir={isRTL ? 'rtl' : 'ltr'}>
        <p className="text-slate-600 font-medium">{t('studentChallenges.notFound')}</p>
        <Link
          to="/student/challenges"
          className="mt-4 inline-flex items-center gap-2 text-violet-700 font-bold hover:underline"
        >
          {t('studentChallenges.backToList')}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-12" dir={isRTL ? 'rtl' : 'ltr'}>
      <Link
        to="/student/challenges"
        className={`inline-flex items-center gap-2 text-sm font-bold text-violet-800 hover:text-violet-950 ${
          isRTL ? 'flex-row-reverse' : ''
        }`}
      >
        <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} aria-hidden />
        {t('studentChallenges.backToList')}
      </Link>

      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">{challenge.name}</h1>
        <div className="flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 capitalize">{challenge.status}</span>
          <span>
            {formatDate(challenge.startsAt)} — {formatDate(challenge.endsAt)}
          </span>
          {challenge.answerType && (
            <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-violet-900">
              {challenge.answerType}
            </span>
          )}
        </div>
      </header>

      {/* مقال WYSIWYG */}
      <article
        className="challenge-wysiwyg rounded-2xl border-[3px] border-slate-900/10 bg-white px-4 py-5 sm:px-6 sm:py-7 shadow-[0_6px_0_0_rgba(15,23,42,0.06)]"
      >
        <div
          className="challenge-wysiwyg-inner text-slate-800 text-[15px] sm:text-base leading-relaxed [&_p]:mb-3 [&_p:last-child]:mb-0 [&_h1]:text-2xl [&_h1]:font-black [&_h1]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:ps-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:ps-5 [&_ol]:mb-3 [&_a]:text-violet-700 [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_iframe]:max-w-full [&_iframe]:rounded-lg [&_blockquote]:border-s-4 [&_blockquote]:border-violet-200 [&_blockquote]:ps-4 [&_blockquote]:italic"
          dangerouslySetInnerHTML={{ __html: html || `<p>${t('studentChallenges.noDescription')}</p>` }}
        />
      </article>

      {/* بعد الإرسال: نتيجة */}
      {submission && (
        <section
          className={`rounded-2xl border-[3px] p-4 sm:p-5 ${
            submission.isCorrect
              ? 'border-emerald-300 bg-emerald-50/90'
              : 'border-rose-300 bg-rose-50/90'
          }`}
        >
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {submission.isCorrect ? (
              <>
                <CheckCircle2 className="h-7 w-7 text-emerald-600 shrink-0" aria-hidden />
                <span className="text-lg font-black text-emerald-900">{t('studentChallenges.resultCorrect')}</span>
              </>
            ) : (
              <>
                <XCircle className="h-7 w-7 text-rose-600 shrink-0" aria-hidden />
                <span className="text-lg font-black text-rose-900">{t('studentChallenges.resultIncorrect')}</span>
              </>
            )}
          </div>
          <p className="text-sm font-medium text-slate-700">
            <span className="font-bold">{t('studentChallenges.yourAnswer')}:</span> {submission.answerValue}
          </p>
          <p className="text-xs text-slate-600 mt-1">
            {t('studentChallenges.submittedAt')}: {formatDate(submission.submittedAt)}
          </p>
        </section>
      )}

      {/* نموذج الإجابة — مثل التعليقات */}
      {readOnly && !submission && (
        <div className="flex items-center gap-2 rounded-2xl border-2 border-amber-200 bg-amber-50 px-4 py-3 text-amber-950">
          <Lock className="h-5 w-5 shrink-0" aria-hidden />
          <p className="text-sm font-semibold">{t('studentChallenges.readOnlyHint')}</p>
        </div>
      )}

      {canSubmit && (
        <section className="rounded-2xl border-[3px] border-slate-900/10 bg-gradient-to-b from-slate-50 to-white p-4 sm:p-5 shadow-inner">
          <h2 className="text-sm font-black uppercase tracking-wide text-slate-500 mb-3">
            {t('studentChallenges.answerSection')}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-3">
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={4}
              className="w-full rounded-xl border-2 border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
              placeholder={t('studentChallenges.answerPlaceholder')}
              disabled={submitting}
            />
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-900/15 bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-2.5 text-sm font-black text-white shadow-md hover:opacity-95 disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Send className="h-4 w-4" aria-hidden />
              )}
              {t('studentChallenges.submitAnswer')}
            </button>
          </form>
        </section>
      )}
    </div>
  );
};

export default StudentChallengeDetail;
